/**
 * InboundForm — 상담 신청 폼 (new design, 다크 카드)
 *
 * preview/index.html 의 .contact 1:1 매칭
 *  - 상단: 서비스 복수 체크박스 (1개 이상 필수)
 *  - 회사명/담당자명/연락처/이메일 (필수) + 사업자등록번호/직원수/메모 (선택)
 *  - 개인정보 수집·이용 동의 (필수)
 *
 * 데이터 흐름:
 *  - React Hook Form + 폼 전용 Zod 스키마 (contactFormSchema)
 *  - 서비스 복수 선택: services 배열은 zod로 검증, 제출 시 첫 번째를 serviceType으로,
 *    전체 라벨은 message 앞에 합쳐 보냄 (어드민에서 한눈에 확인 가능)
 *  - 제출 → api/client.ts 의 submitInquiry()
 *    · VITE_API_MODE='firestore' 일 때 Firestore 직접 write
 *    · 'mock' 일 때 가짜 응답
 */
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { SERVICE_TYPES, SERVICE_LIST, type ServiceType } from '@/config/constants';
import { submitInquiry, ApiError } from '@/api/client';
import { useToast } from '@/hooks/useToast';
import { useBusinessVerify } from '@/hooks/useBusinessVerify';
import { formatPhone, formatBusinessNumber } from '@/utils/format';
import type { InquiryPayload } from '@/types/inquiry';

// ================================================================
// 폼 전용 Zod 스키마
// - schemas/inquiry.ts 의 inboundInquirySchema 와 동일한 규칙
// - 단, serviceType은 services 배열 첫 번째로부터 자동 계산되므로 폼에서는 제외
// ================================================================
const contactFormSchema = z.object({
  services: z
    .array(z.enum(SERVICE_TYPES))
    .min(1, '상담 서비스를 1개 이상 선택해 주세요'),
  companyName: z
    .string()
    .min(1, '회사명을 입력해 주세요')
    .max(100, '회사명은 100자를 넘을 수 없습니다'),
  representativeName: z
    .string()
    .min(1, '이름을 입력해 주세요')
    .max(50, '이름은 50자를 넘을 수 없습니다'),
  contactPhone: z
    .string()
    .min(1, '연락처를 입력해 주세요')
    .regex(/^010-?\d{4}-?\d{4}$/, '올바른 휴대폰 번호 형식이 아닙니다'),
  contactEmail: z.string().email('올바른 이메일 주소를 입력해 주세요'),
  businessNumber: z
    .string()
    .regex(/^\d{3}-?\d{2}-?\d{5}$/, '사업자등록번호 10자리를 정확히 입력해 주세요')
    .optional()
    .or(z.literal('')),
  employeeCount: z.string().max(50).optional().or(z.literal('')),
  message: z.string().max(2000).optional().or(z.literal('')),
  privacyConsent: z.literal(true, {
    errorMap: () => ({ message: '개인정보 수집·이용에 동의해 주세요' }),
  }),
});

type ContactFormValues = z.infer<typeof contactFormSchema>;

export interface InboundFormProps {
  /** 개인정보 동의 링크 클릭 → 모달 또는 페이지 */
  onOpenPrivacy?: () => void;
}

export function InboundForm({ onOpenPrivacy }: InboundFormProps) {
  const { showToast } = useToast();
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      services: [],
      companyName: '',
      representativeName: '',
      contactPhone: '',
      contactEmail: '',
      businessNumber: '',
      employeeCount: '',
      message: '',
      // boolean → literal(true) 캐스팅 — 미체크 상태 표현
      privacyConsent: false as unknown as true,
    },
  });

  const servicesValue = watch('services');
  const businessNumberValue = watch('businessNumber');

  // 사업자번호 국세청 진위확인 (blur 시 호출)
  const bizVerify = useBusinessVerify(businessNumberValue);

  /** 서비스 체크박스 토글 (RHF 외부 상태 → setValue) */
  const toggleService = (id: ServiceType) => {
    const cur = new Set(servicesValue);
    if (cur.has(id)) cur.delete(id);
    else cur.add(id);
    setValue('services', Array.from(cur), { shouldValidate: true });
  };

  /** 전화/사업자번호 자동 포맷 */
  const onPhoneChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setValue('contactPhone', formatPhone(e.target.value), { shouldValidate: false });
  };
  const onBizChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setValue('businessNumber', formatBusinessNumber(e.target.value), { shouldValidate: false });
  };

  /** 폼 초기값 (제출 후 reset에 재사용) */
  const initialValues: ContactFormValues = {
    services: [],
    companyName: '',
    representativeName: '',
    contactPhone: '',
    contactEmail: '',
    businessNumber: '',
    employeeCount: '',
    message: '',
    privacyConsent: false as unknown as true,
  };

  /** 제출 */
  const onSubmit = async (data: ContactFormValues) => {
    if (submitting) return;

    // 사업자번호를 입력한 경우에 한해, 진위확인 실패 시 제출 차단
    // (InboundForm은 사업자번호가 선택 항목이라 빈 값이면 검증 안 함)
    if (data.businessNumber && bizVerify.isError) {
      showToast({
        type: 'error',
        message:
          bizVerify.status === 'not_found'
            ? '국세청에 등록되지 않은 사업자번호입니다. 다시 확인해 주세요.'
            : bizVerify.status === 'closed'
            ? '폐업 처리된 사업자번호입니다. 다시 확인해 주세요.'
            : bizVerify.message || '사업자 정보를 다시 확인해 주세요.',
      });
      return;
    }

    setSubmitting(true);
    try {
      // 복수 선택 → 메인 serviceType은 첫 번째, 전체 라벨은 message 앞에 합쳐 보냄
      const mainServiceId = data.services[0] as ServiceType;
      const allLabels = data.services
        .map((id) => SERVICE_LIST.find((s) => s.id === id)?.label ?? id)
        .join(', ');
      const messageMerged = `[관심 서비스: ${allLabels}]\n${data.message ?? ''}`.trim();

      const payload: InquiryPayload = {
        channel: 'inbound',
        serviceType: mainServiceId,
        companyName: data.companyName,
        representativeName: data.representativeName,
        contactPhone: data.contactPhone,
        contactEmail: data.contactEmail,
        businessNumber: data.businessNumber || undefined,
        employeeCount: data.employeeCount || undefined,
        message: messageMerged,
        privacyConsent: true,
      };

      await submitInquiry(payload);

      showToast({
        type: 'success',
        message: '상담 신청이 접수되었습니다. 영업일 24시간 이내에 연락드리겠습니다.',
        duration: 5000,
      });
      reset(initialValues);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : '접수 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.';
      showToast({ type: 'error', message: msg });
    } finally {
      setSubmitting(false);
    }
  };

  /** 개인정보 링크 클릭 — onOpenPrivacy 있으면 모달, 없으면 새 창에서 /privacy */
  const handlePrivacy = (e: React.MouseEvent) => {
    if (onOpenPrivacy) {
      e.preventDefault();
      onOpenPrivacy();
    }
  };

  return (
    <section className="contact" id="contact">
      <div className="section-head">
        <span className="section-tag">CONTACT</span>
        <h2 className="section-title"><span className="accent">상담 신청</span>하기</h2>
        <p className="section-sub">
          영업일 기준 24시간 이내에 담당 컨설턴트가 연락드립니다. 초기 상담은 무료로 진행됩니다.
        </p>
      </div>

      <div className="contact-inner">
        <div className="contact-checks">
          <span className="contact-check"><span className="ck">✓</span> 기업 맞춤 컨설팅</span>
          <span className="contact-check"><span className="ck">✓</span> 인증 사후관리 지원</span>
          <span className="contact-check"><span className="ck">✓</span> 초기 상담 비용 무료</span>
        </div>

        <form className="contact-form" onSubmit={handleSubmit(onSubmit)} noValidate>
          {/* 상담 서비스 (복수 체크박스) */}
          <div className="form-services">
            <div className="form-services-label">
              상담 서비스<span className="req">*</span>
              <span className="form-services-hint">(복수 선택 가능)</span>
            </div>
            <div className="form-services-grid">
              {SERVICE_LIST.map((svc) => {
                const checked = servicesValue.includes(svc.id);
                return (
                  <label key={svc.id} className="svc-check">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleService(svc.id)}
                    />
                    <span className="svc-box" />
                    <span className="svc-text">{svc.label}</span>
                  </label>
                );
              })}
            </div>
            {errors.services && (
              <div className="form-error" style={{ marginTop: 10 }}>
                {errors.services.message}
              </div>
            )}
          </div>

          {/* 입력 필드 그리드 */}
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label" htmlFor="cf-company">
                회사명<span className="req">*</span>
              </label>
              <input
                id="cf-company"
                className={`form-input${errors.companyName ? ' invalid' : ''}`}
                placeholder="예: 주식회사 OOO"
                {...register('companyName')}
              />
              {errors.companyName && (
                <div className="form-error">{errors.companyName.message}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="cf-name">
                담당자 이름<span className="req">*</span>
              </label>
              <input
                id="cf-name"
                className={`form-input${errors.representativeName ? ' invalid' : ''}`}
                placeholder="예: 홍길동"
                {...register('representativeName')}
              />
              {errors.representativeName && (
                <div className="form-error">{errors.representativeName.message}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="cf-phone">
                연락처<span className="req">*</span>
              </label>
              <input
                id="cf-phone"
                type="tel"
                inputMode="numeric"
                className={`form-input${errors.contactPhone ? ' invalid' : ''}`}
                placeholder="예: 010-1234-5678"
                {...register('contactPhone')}
                onChange={onPhoneChange}
              />
              {errors.contactPhone && (
                <div className="form-error">{errors.contactPhone.message}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="cf-email">
                이메일<span className="req">*</span>
              </label>
              <input
                id="cf-email"
                type="email"
                className={`form-input${errors.contactEmail ? ' invalid' : ''}`}
                placeholder="예: contact@company.com"
                {...register('contactEmail')}
              />
              {errors.contactEmail && (
                <div className="form-error">{errors.contactEmail.message}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="cf-biz">사업자등록번호</label>
              <input
                id="cf-biz"
                inputMode="numeric"
                className={`form-input${
                  errors.businessNumber || bizVerify.isError ? ' invalid' : ''
                }`}
                placeholder="예: 123-45-67890"
                {...register('businessNumber')}
                onChange={onBizChange}
                onBlur={bizVerify.onBlur}
              />
              {errors.businessNumber && (
                <div className="form-error">{errors.businessNumber.message}</div>
              )}
              {!errors.businessNumber && bizVerify.status !== 'idle' && (
                <div
                  className={`form-${bizVerify.isError ? 'error' : 'hint'}`}
                  style={
                    bizVerify.status === 'checking'
                      ? { color: '#94A3B8' }
                      : bizVerify.status === 'valid'
                      ? { color: '#10B981' }
                      : undefined
                  }
                >
                  {bizVerify.message}
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="cf-emp">직원수(4대보험 가입자수)</label>
              <input
                id="cf-emp"
                className="form-input"
                placeholder="예: 12명"
                {...register('employeeCount')}
              />
            </div>

            <div className="form-group full">
              <label className="form-label" htmlFor="cf-msg">문의 내용</label>
              <textarea
                id="cf-msg"
                className="form-textarea"
                placeholder="궁금한 점을 자유롭게 작성해주세요."
                {...register('message')}
              />
            </div>
          </div>

          <label className="form-agree">
            <input type="checkbox" {...register('privacyConsent')} />
            <span>
              <a
                href="/privacy"
                onClick={handlePrivacy}
                target={onOpenPrivacy ? undefined : '_blank'}
                rel="noreferrer"
              >
                개인정보 수집·이용
              </a>
              에 동의합니다 (필수)
            </span>
          </label>
          {errors.privacyConsent && (
            <div className="form-error">{errors.privacyConsent.message as string}</div>
          )}

          <button type="submit" className="form-submit" disabled={submitting}>
            {submitting ? '접수 중...' : '상담 신청하기 →'}
          </button>
        </form>
      </div>
    </section>
  );
}
